import { PriceZoneSystem } from '@schuelerhilfe/shared/src/enums/priceZoneSystem';
import {
  buildZonePriceMap,
  findActivePriceZone,
} from '@schuelerhilfe/shared/src/pricing/priceZones';
import { derivedPrivacyPolicyPath } from '@schuelerhilfe/shared/src/seo/privacyPolicyPath';
import { applyGlossary } from './glossary';
import type { ContextEntities } from './RenderContext';
import { resolveState } from './resolveState';
import type { DomainKey } from '@/lib/domain-config';
import { resolveCityPrice } from '@/lib/pricing/resolveCityPrice';

// Replaces `{path.to.field}` in a template string with values resolved
// against the `entities` bag. Unknown paths pass through unchanged so
// editors see the literal token (and a dev console warning) instead of
// silently empty text.
//
// Price placeholders: `{location.<placeholder>}` tokens whose second
// segment matches a `price.placeholder` value in the location's price
// zones are resolved to the formatted price (e.g. "9,35 €"). The flat
// map is built once per render from the first non-disabled zone matching
// the active system (SH or ERNA, derived from the `domain:*` tag).
//
// Glossary substitution runs *after* placeholder resolution so a
// placeholder that resolves to a glossary-eligible term is still localized.

// The `<placeholder>` may contain dots, square brackets, and hyphens — real
// price placeholders look like `[price_99_9]` or `F1-Platzhalter`. Kept in sync
// with resolveHeroBadge's token grammar. Unresolvable tokens pass through as
// their literal `{...}` form.
const PLACEHOLDER_RE = /\{([\w.[\]-]+)\}/g;

/**
 * Entities a URL *routes to*, which a valid render may legitimately lack: the
 * magazine index has no category, an article page has no author when the
 * relation is empty. 30.03.05 § RenderContext forbids substituting a frozen
 * empty-string entity for them, and 10.02.05 § AC 5 asks the widget using the
 * placeholder to fall through — so their tokens resolve to the empty string.
 *
 * Everything else keeps passing through as its literal `{...}` token: an
 * unknown `{location.foo}` is an editor typo, and hiding it would leave the
 * editor with no signal at all.
 *
 * `article` is listed although nothing populates it before wave 3; an
 * unpopulated prefix costs nothing, since the token simply blanks.
 */
const BLANKING_ENTITY_PREFIXES = new Set(['category', 'author', 'article']);

const blanksWhenAbsent = (path: string): boolean =>
  BLANKING_ENTITY_PREFIXES.has(path.split('.')[0] ?? '');

export interface SubstitutionContext {
  entities: ContextEntities;
  tagBag: string[];
}

/**
 * Resolves `{path.to.field}` placeholders in `template` against the render
 * context: price tokens resolve first — `{location.<placeholder>}` from the
 * location's price zones, `{city.<placeholder>}` from the city-level price map
 * on City Page renders — then `{domain.*}` from the Domain Setting's editor-
 * maintained placeholders, then `{state.*}`, then arbitrary entity paths, then
 * glossary substitution. Unknown paths pass through as their literal `{...}`
 * form.
 */
export function substitutePlaceholders(
  template: string,
  ctx: SubstitutionContext,
): string {
  if (!template) {
    return template;
  }

  const priceMap = buildPricePlaceholderMap(ctx);
  // A Bundesland has no entity for `readPath` to walk — see `resolveState`.
  const state = resolveState(ctx.entities, ctx.tagBag);

  const placeholderResolved = template.replace(
    PLACEHOLDER_RE,
    (_, path: string) => {
      // The Standort's own Datenschutzerklärung, derived rather than stored:
      // one slug-less Location Template Page backs every Standort, so no
      // editor-entered value can be right for GmbH *and* franchise. Resolves
      // ahead of the price map so a price placeholder cannot shadow it.
      //
      // Falls back to the domain-level policy exactly as School Details'
      // tier 3 does — a Standort without a `centerNumber`, or a render with no
      // location at all (City Page, page shell), has no per-location page, and
      // a literal `{...}` token inside an `href` is a broken link rather than
      // the visible-gap signal it is in body text.
      if (path === 'location.privacyPolicyPath') {
        return (
          derivedPrivacyPolicyPath(ctx.entities.location ?? {}) ??
          ctx.entities.privacyPolicyUrl ??
          `{${path}}`
        );
      }
      // Check price placeholder map first (location.<placeholder>)
      if (path.startsWith('location.')) {
        const key = path.slice('location.'.length);
        const priceValue = priceMap.get(key);
        if (priceValue != null) {
          return priceValue;
        }
      }
      // City Page counterpart — resolves against the city-level price map.
      if (path.startsWith('city.')) {
        const cityPrice = resolveCityPrice(
          ctx.entities.city?.prices,
          path.slice('city.'.length),
        );
        if (cityPrice != null) {
          return cityPrice;
        }
      }
      // Domain Setting placeholders. These resolve *before* `readPath` walks
      // `entities.domain`, so a record named `phone` or `matelsoPhoneLabel`
      // overrides the 70.07 phone chain — the agreed precedence, and the
      // reason the CMS field warns about those two names.
      if (path.startsWith('domain.')) {
        // Own-property lookup, not a plain index: a placeholder bag inherits
        // `toString`, `constructor` and friends from `Object.prototype`, and
        // `{domain.toString}` — a name no brand defines — would otherwise
        // render `function toString() { [native code] }` into the page instead
        // of falling through to the literal token.
        const bag = ctx.entities.domain?.placeholders;
        const key = path.slice('domain.'.length);
        const domainValue =
          bag && Object.prototype.hasOwnProperty.call(bag, key)
            ? bag[key]
            : undefined;
        if (typeof domainValue === 'string') {
          return domainValue;
        }
      }
      if (path.startsWith('state.')) {
        const key = path.slice('state.'.length);
        if (state && (key === 'name' || key === 'slug')) {
          return state[key];
        }
      }
      const value = readPath(ctx.entities, path);
      // Objects (e.g. the City's rich-text-blocks fields, or the author's
      // `description`) cannot be inlined into a string — they resolve only via
      // the block-level splice in `spliceBlockPlaceholders`. Pass the literal
      // token through instead of emitting "[object Object]".
      //
      // The blanking rule runs first and on `null`/`undefined` only — blanking
      // is for an entity (or field) a valid render legitimately *lacks*, and
      // `typeof null` is `'object'` too. A rich-text field that is *present* is
      // not a missing value: 30.03.03 declares no `{author.description}` token
      // and 10.02.06 § Testing Guide asks for that text "unchanged", which is
      // the literal-token branch below.
      if (value == null) {
        // The 70.07 phone chain, for the two tokens that name its pair. The
        // `domain` entity carries the chain's own result — the Standort's
        // number and label when it has them, the Domain Setting's when it does
        // not — so reading it here gives a Standort without a phone the
        // domain's pair instead of a literal token.
        //
        // The pair cannot split: when the Standort has a number, the chain
        // already put *its* label (or none) on the entity, so a missing label
        // stays missing rather than borrowing the domain's, which would name a
        // number different from the one being dialled.
        if (
          path === 'location.phone' ||
          path === 'location.matelsoPhoneLabel'
        ) {
          const fromChain =
            ctx.entities.domain?.[
              path.slice('location.'.length) as 'phone' | 'matelsoPhoneLabel'
            ];
          if (fromChain) {
            return fromChain;
          }
        }
        return blanksWhenAbsent(path) ? '' : `{${path}}`;
      }
      if (typeof value === 'object') {
        return `{${path}}`;
      }
      if (typeof value === 'number') {
        return Number.isInteger(value)
          ? String(value)
          : value.toLocaleString('de-DE');
      }
      return String(value);
    },
  );
  // Glossary substitution: replace domain-specific terms (e.g. Abitur → Matura for AT).
  const domainTag = ctx.tagBag.find((t) => t.startsWith('domain:'));
  const domainKey = domainTag?.slice('domain:'.length) as DomainKey | undefined;
  return domainKey
    ? applyGlossary(placeholderResolved, domainKey)
    : placeholderResolved;
}

/**
 * Builds a flat map of `placeholder → formatted price` from the
 * location's price zones. Uses the first non-disabled zone matching
 * the active system. First match wins for duplicate placeholder names.
 */
function buildPricePlaceholderMap(
  ctx: SubstitutionContext,
): Map<string, string> {
  const domainTag = ctx.tagBag.find((t) => t.startsWith('domain:'));
  const domainTagValue = domainTag?.slice('domain:'.length);
  // Tag value is 'erna' (not 'ien') for the Erste Nachhilfe domain.
  const system =
    domainTagValue === 'erna' ? PriceZoneSystem.ERNA : PriceZoneSystem.SH;
  const zone = findActivePriceZone(ctx.entities.location?.priceZones, system);
  return zone ? buildZonePriceMap(zone) : new Map();
}

/**
 * Reads a dotted `path` (e.g. `location.name`) off `root`, or `undefined`.
 *
 * **Own properties only.** Every bag this walks is JSON — a DTO from the
 * backend or the context bag assembled around it — so an own-property read is
 * what the token grammar means in the first place. A plain index would also
 * reach `Object.prototype`, and since a function value falls through to
 * `String(value)` in the caller, `{location.toString}` or
 * `{domain.constructor}` would render `function toString() { [native code] }`
 * into the page instead of passing through as the literal token an unknown
 * name is supposed to produce.
 */
export function readPath(root: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = root;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') {
      return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(cur, p)) {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}
